import { ActionButton, Button, StatusLight, Text } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useCallback, useMemo, type ReactNode } from 'react';

import { useExpressApiContext } from '@/api/ExpressApiContext';
import {
	ExpressLogo,
	InstagramLogo,
	SquareLogo,
} from '@/components/icons/socials-monochrome';
import { useUserSettings } from '@/hooks/useUserSettings';
import { logger } from '@/logger';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { trackEvent } from '@/utils/fullstory';

// Matches the backend's INSTAGRAM_TOKEN_EXPIRY_LOOKAHEAD_DAYS (instagram_sync.py).
const TOKEN_EXPIRY_LOOKAHEAD_DAYS = 7;

const isExpiringWithinLookahead = (
	...expiryTimestamps: Array<string | null | undefined>
): boolean => {
	const lookahead =
		Date.now() + TOKEN_EXPIRY_LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000;
	return expiryTimestamps.some((ts) => {
		if (!ts) return false; // null/absent = never expires
		const expiry = new Date(ts).getTime();
		return !Number.isNaN(expiry) && expiry <= lookahead;
	});
};

const RECONNECT_REASON_LABELS: Record<string, string> = {
	missing_scopes: 'Reconnect needed — missing insights permission',
	token_expired: 'Reconnect needed — connection expired',
	token_expiring: 'Reconnect needed — connection expires soon',
};
const RECONNECT_DEFAULT_LABEL = 'Reconnect needed — connection expires soon';

// Named per product, unlike RECONNECT_REASON_LABELS above: a merchant whose Instagram and
// Square are both degrading would otherwise read two character-identical warnings, and a
// screen-reader user gets no row context at all.
const SQUARE_DISCONNECTED_LABEL =
	'Disconnected — Square stopped sharing your sales data';
// Names the actor. "Disconnected" alone is the word this row already uses for the merchant's
// OWN Disconnect button, so on its own it cannot tell "you did this" from "Square ended it".
const SQUARE_REVOKED_LABEL = 'Square ended our access — reconnect needed';
const SQUARE_CALLOUT_HEADING = 'Square stopped sharing your sales data';

// A cold sync runs at connect time, so an empty rollup right after connecting means the sync
// is still working. Past this window it means the merchant simply has not rung anything up —
// sales_data_synced_at is stamped only when the rollup holds orders, so a zero-sales merchant
// would otherwise read "first sync in progress" forever.
const FIRST_SYNC_GRACE_HOURS = 24;

// Same instant in different serializations ("Z" vs "+00:00") must compare equal.
const isSameInstant = (
	a: string | null | undefined,
	b: string | null | undefined,
): boolean => {
	if (!a || !b) return false;
	const ta = Date.parse(a);
	const tb = Date.parse(b);
	return !Number.isNaN(ta) && ta === tb;
};

interface IntegrationRowProps {
	logo: ReactNode;
	name: string;
	description: string;
	statusLabel: string;
	connected: boolean;
	onConnect: () => void;
	onDisconnect: () => void;
	connectDisabled?: boolean;
	onReconnect?: () => void;
	statusVariant?: 'positive' | 'neutral' | 'notice' | 'negative';
	// A second muted line under the StatusLight. The light carries connection HEALTH and
	// has room for one string; freshness is a different fact and needs its own slot.
	detail?: string;
	// A standing statement of consequence, shown when the integration has stopped working in
	// a way only the merchant can fix. Deliberately a block in the row rather than a floating
	// pill: this is where the Reconnect control already lives, so the explanation and the
	// remedy are in one place, and it cannot occlude the app chrome.
	callout?: ReactNode;
	// Keep Disconnect available while Reconnect is offered. Off by default, because for most
	// rows "needs reconnecting" is a transient token problem and one action is the clearer
	// row. It is on for a row whose reconnect prompt can be raised by the merchant's own
	// deliberate choice: someone who disconnects us from inside the provider's dashboard
	// meant to leave, and offering only Reconnect strands them with a notice they cannot
	// clear without first re-authorizing the thing they just withdrew.
	allowDisconnectWhileReconnecting?: boolean;
}

const IntegrationRow = ({
	logo,
	name,
	description,
	statusLabel,
	connected,
	onConnect,
	onDisconnect,
	connectDisabled = false,
	onReconnect,
	statusVariant,
	detail,
	callout,
	allowDisconnectWhileReconnecting = false,
}: IntegrationRowProps) => {
	const testId = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
	return (
		<div
			className="glass-card-sm p-6"
			data-testid={`integration-row-${testId}`}
		>
			{/* The identity and action line stays one row. A callout is a SIBLING below it,
			    not a child of the text column, so it spans the card's full width instead of
			    being boxed into the space left over beside the button.

			    `gap-6` rather than `justify-between` alone: with only the latter the text column
			    grows until it touches the button, and every row's description is long enough to
			    do it. A minimum gutter belongs on the row, so it holds for every integration
			    rather than being tuned per row. */}
			<div className="flex items-center justify-between gap-6">
				<div className="flex min-w-0 items-center gap-3">
					<div className="flex size-6 shrink-0 items-center justify-center">
						{logo}
					</div>
					{/* min-w-0 so this column may shrink below its content width. A flex child
					    defaults to min-width:auto, which refuses to shrink past the longest
					    unbreakable run and pushes into the gutter instead of wrapping. */}
					<div className="flex min-w-0 flex-col gap-1.5">
						<Text
							styles={style({
								color: 'title',
								fontSize: 'body',
								fontWeight: 'bold',
							})}
						>
							{name}
						</Text>
						<Text
							styles={style({
								color: 'body',
								fontSize: 'body-sm',
							})}
						>
							{description}
						</Text>
						{/* Suppressed while a callout shows. The callout already leads with the
						    same fact in plainer words, and a red status light stacked above a red
						    banner saying the same thing reads as two separate problems. */}
						{!callout && (
							<>
								<div data-testid={`integration-status-${testId}`}>
									<StatusLight
										size="S"
										variant={
											statusVariant ?? (connected ? 'positive' : 'neutral')
										}
									>
										{statusLabel}
									</StatusLight>
								</div>
								{detail ? (
									<div>
										{/* A measured fact, not prose: the chip separates "when data last
										    arrived" from the description above it, which is marketing copy.
										    12px/16px to match the StatusLight text directly above it — the two
										    are one thought about the connection, so a size step between them
										    would imply a hierarchy that isn't there. */}
										<span
											className="inline-block rounded-md bg-gray-100 px-2 py-0.5
												text-xs/4 text-gray-600"
											data-testid={`integration-detail-${testId}`}
										>
											{detail}
										</span>
									</div>
								) : null}
							</>
						)}
					</div>
				</div>
				<div className="flex shrink-0 items-center justify-end gap-2">
					{/* Reconnect wins over both other actions: while re-auth is needed it is the
					    only useful action, and a lapsed connection that offers "Connect" reads
					    as "you never set this up" to a merchant who knows they did. */}
					{onReconnect ? (
						<>
							{/* Disconnect sits BEFORE Reconnect so the primary action keeps the
							    rightmost slot it holds in every other state of every other row.
							    It renders only for a row that opted in — see the prop comment. */}
							{allowDisconnectWhileReconnecting ? (
								<ActionButton
									onPress={onDisconnect}
									isQuiet
									UNSAFE_className="btn-destructive-quiet"
								>
									Disconnect
								</ActionButton>
							) : null}
							<Button variant="primary" onPress={onReconnect}>
								Reconnect
							</Button>
						</>
					) : connected ? (
						<ActionButton
							onPress={onDisconnect}
							isQuiet
							UNSAFE_className="btn-destructive-quiet"
						>
							Disconnect
						</ActionButton>
					) : (
						<Button
							variant="primary"
							onPress={onConnect}
							isDisabled={connectDisabled}
						>
							Connect
						</Button>
					)}
				</div>
			</div>
			{callout}
		</div>
	);
};

export const ConnectedAccounts = memo(function ConnectedAccounts() {
	const {
		settings,
		patchSettings,
		isInstagramConnected,
		isSquareConnected,
		connectInstagram,
		connectSquare,
		disconnectInstagram,
		disconnectSquare,
	} = useUserSettings();
	const { expressApi, hasValidInstagramConnection, beginExpressConnection } =
		useExpressApiContext();

	const instagramConnection = settings?.instagram_connection ?? null;
	const squareConnection = settings?.square_connection ?? null;

	const instagramLabel = useMemo(() => {
		if (!isInstagramConnected || !instagramConnection) return 'Not connected';
		return instagramConnection.ig_username
			? `@${instagramConnection.ig_username.replace(/^@/, '')}`
			: 'Connected';
	}, [instagramConnection, isInstagramConnected]);

	// A connection that exists but no longer authenticates. Square's refresh token dies 30
	// days after the last refresh, and until now that state rendered as the pristine
	// "Not connected" / "Connect" pair — which reads as "you never set this up" to a merchant
	// who knows they did.
	const squareIsDisconnected = squareConnection != null && !isSquareConnected;

	// Square told us our access was withdrawn. This is the ONLY Square state a merchant has to
	// act on, and there is deliberately no "expires soon" rung beside it: Square's code-flow
	// refresh tokens do not expire ("a refresh token only becomes invalid when the
	// application's access has been completely revoked"), and the daily job renews the access
	// token automatically. An expiry countdown could therefore only fire once renewal had
	// already been failing for weeks, while nagging merchants whose connection was fine.
	const squareRevokedAt = squareConnection?.square_revoked_at ?? null;
	const squareIsRevoked = squareConnection != null && !!squareRevokedAt;

	const squareNeedsReconnect = squareIsDisconnected || squareIsRevoked;

	const squareLabel = useMemo(() => {
		if (squareIsRevoked) return SQUARE_REVOKED_LABEL;
		if (squareIsDisconnected) return SQUARE_DISCONNECTED_LABEL;
		if (!isSquareConnected || !squareConnection) return 'Not connected';
		// The merchant's own name for their business, not the raw merchant id: `MLE1F4KQ8Z2`
		// is unverifiable to the person reading it. Persisted by the sync, so it still renders
		// once the token is dead — the state where the row most needs to name something.
		return squareConnection.square_location_name?.trim() || 'Connected';
	}, [
		isSquareConnected,
		squareConnection,
		squareIsDisconnected,
		squareIsRevoked,
	]);

	// The standing explanation, in the row that carries the Reconnect control. It leads with
	// what stopped rather than with an instruction, and says plainly that the gap will not be
	// filled in — promising a backfill we do not do would turn one outage into a support
	// conversation about data we said we would recover.
	const squareCallout = useMemo(() => {
		if (!squareIsRevoked) return undefined;
		// formatRelativeTime yields "Just now" for a fresh timestamp, which reads as a
		// grammatical error after "stopped arriving" — and a merchant told it stopped "just
		// now" learns nothing from the phrase anyway. Say it plainly instead.
		const relative = squareRevokedAt
			? formatRelativeTime(squareRevokedAt)
			: undefined;
		const stoppedOn =
			relative && !/just now/i.test(relative) ? relative : undefined;
		return (
			<div
				role="region"
				// A landmark rather than a bare status line: this holds the control that fixes
				// the problem, so a screen-reader user needs to be able to navigate BACK to it,
				// not just hear it announced once. Labelled directly because the label text and
				// the heading are the same string.
				aria-label={SQUARE_CALLOUT_HEADING}
				// Full width of the card. It was capped at max-w-md while it lived inside the
				// text column beside the button; as a sibling of that row it fills the card,
				// which suits a standing statement of consequence better than a narrow box.
				className="mt-4 rounded-lg bg-red-50 p-3"
			>
				<Text
					styles={style({
						color: 'negative',
						fontSize: 'body-sm',
						fontWeight: 'bold',
					})}
				>
					{SQUARE_CALLOUT_HEADING}
				</Text>
				<div aria-live="polite" aria-atomic="true">
					<Text styles={style({ color: 'negative', fontSize: 'body-sm' })}>
						{stoppedOn
							? `Your sales data stopped arriving ${stoppedOn}. Reconnect Square to start receiving it again — the sales from while it was disconnected won't be recovered.`
							: `Your sales data has stopped arriving. Reconnect Square to start receiving it again — the sales from while it was disconnected won't be recovered.`}
					</Text>
				</div>
			</div>
		);
	}, [squareIsRevoked, squareRevokedAt]);

	// The second line: when sales data last actually arrived. Deliberately not the token's
	// last_refresh_at — a token can refresh daily while no order has flowed in a month, which
	// is the exact lie this change exists to stop telling.
	const squareDetail = useMemo(() => {
		if (!squareConnection) return undefined;
		if (squareConnection.sales_data_synced_at) {
			return `Sales data updated ${formatRelativeTime(squareConnection.sales_data_synced_at)}`;
		}
		// `undefined` (the key is absent from the payload) and `null` (the key arrived and
		// is genuinely empty) are different facts, not two spellings of "no value" -- an
		// older db-api that predates this field never sends the key at all, and reads
		// identically falsy to a merchant with a working connection and zero sales unless
		// they're told apart here. Confidently telling a working merchant "No sales data
		// yet" during that rollout window is the exact false negative this component
		// otherwise exists to avoid (see the revoked/expired handling above).
		if (squareConnection.sales_data_synced_at === undefined) {
			return 'Sales status unknown';
		}
		// formatRelativeTime returns the bare string "Unknown" for an absent date, which to a
		// merchant who just connected successfully is the least honest string in the row.
		const connectedAt = Date.parse(squareConnection.connected_at);
		const withinFirstSync =
			!Number.isNaN(connectedAt) &&
			Date.now() - connectedAt < FIRST_SYNC_GRACE_HOURS * 60 * 60 * 1000;
		return withinFirstSync ? 'First sync in progress' : 'No sales data yet';
	}, [squareConnection]);

	const expressInstagramChannel = useMemo(
		() =>
			settings?.express_connection?.calendar?.[0]?.channels.find(
				(ch) => ch.type === 'instagram',
			),
		[settings?.express_connection],
	);

	const expressLabel = useMemo(() => {
		if (!hasValidInstagramConnection) return 'Not connected';
		const calendar = settings?.express_connection?.calendar?.[0];
		if (!calendar) return 'Connected';
		const igChannel = calendar.channels.find(
			(ch) =>
				ch.type === 'instagram' &&
				ch.connection &&
				new Date(ch.connection.expires) > new Date(),
		);
		return igChannel?.connection?.name
			? `Instagram · ${igChannel.connection.name}`
			: 'Connected';
	}, [hasValidInstagramConnection, settings?.express_connection]);

	const isExpressInstagramExpiringSoon = useMemo(
		() =>
			isExpiringWithinLookahead(expressInstagramChannel?.connection?.expires),
		[expressInstagramChannel],
	);

	const handleConnectExpress = useCallback(() => {
		trackEvent('Express Connect Clicked');
		logger.info('Opening Express channel connector');
		beginExpressConnection();
	}, [beginExpressConnection]);

	const handleReconnectExpress = useCallback(() => {
		trackEvent('Express Instagram Reconnect Clicked');
		beginExpressConnection();
	}, [beginExpressConnection]);

	const handleDisconnectExpress = useCallback(() => {
		trackEvent('Express Disconnected');
		logger.info('Disconnecting Express account');
		void (async () => {
			if (!expressApi) {
				logger.error('Cannot disconnect Express: no API client');
				return;
			}

			const calendar = settings?.express_connection?.calendar?.[0];
			if (!calendar) {
				logger.error('Cannot disconnect Express: no calendar');
				return;
			}

			const instagramChannel = calendar.channels.find(
				(ch) => ch.type === 'instagram',
			);

			try {
				if (instagramChannel) {
					await expressApi.deleteConnection(calendar.id, instagramChannel.id);
				}

				const calendars = await expressApi.getCalendars();
				const result = await patchSettings({
					express_connection:
						calendars.length > 0
							? {
									calendar: calendars.map((c) => ({
										id: c.id,
										name: c.name,
										channels: c.channels,
									})),
								}
							: null,
				});
				if (!result) {
					logger.error('Failed to update Express connection in settings');
				}
			} catch (error) {
				logger.error('Failed to disconnect Express account', error);
			}
		})();
	}, [expressApi, patchSettings, settings?.express_connection]);

	const handleConnectInstagram = useCallback(() => {
		trackEvent('Instagram Connect Clicked');
		connectInstagram();
	}, [connectInstagram]);

	const handleReconnectInstagram = useCallback(() => {
		trackEvent('Instagram Reconnect Clicked');
		connectInstagram();
	}, [connectInstagram]);

	// Reconnect is needed when either signal is active (fully expired tokens
	// flip isInstagramConnected false, which shows Connect instead):
	// 1. the server's reauth-prompt record from the daily sync, if it targets
	//    the CURRENT connection (covers missing_scopes, which only the server's
	//    debug_token check can detect; stale records from before a reconnect
	//    are ignored);
	// 2. the local check that the token expires within 7 days (catches
	//    expiring tokens before the daily sync has run).
	const activeReauthPrompt = useMemo(() => {
		const prompt = settings?.reauth_prompts?.instagram;
		if (!prompt || !instagramConnection) return null;
		return isSameInstant(
			prompt.connection_generation,
			instagramConnection.connected_at,
		)
			? prompt
			: null;
	}, [settings?.reauth_prompts?.instagram, instagramConnection]);

	const isInstagramExpiringSoon = useMemo(
		() =>
			isExpiringWithinLookahead(
				instagramConnection?.ig_token_expires_at,
				instagramConnection?.data_access_expires_at,
			),
		[instagramConnection],
	);

	const instagramNeedsReconnect =
		isInstagramConnected && (!!activeReauthPrompt || isInstagramExpiringSoon);

	const instagramReconnectLabel = activeReauthPrompt
		? (RECONNECT_REASON_LABELS[activeReauthPrompt.reason] ??
			RECONNECT_DEFAULT_LABEL)
		: RECONNECT_DEFAULT_LABEL;

	const handleDisconnectInstagram = useCallback(() => {
		trackEvent('Instagram Disconnected');
		void disconnectInstagram();
	}, [disconnectInstagram]);

	const handleConnectSquare = useCallback(() => {
		trackEvent('Square Connect Clicked');
		connectSquare();
	}, [connectSquare]);

	const handleReconnectSquare = useCallback(() => {
		trackEvent('Square Reconnect Clicked');
		connectSquare();
	}, [connectSquare]);

	const handleDisconnectSquare = useCallback(() => {
		trackEvent('Square Disconnected');
		void disconnectSquare();
	}, [disconnectSquare]);

	return (
		<div className="flex flex-col gap-4">
			<IntegrationRow
				logo={<ExpressLogo className="h-5 w-5" />}
				name="Instagram via Express"
				description="Schedule posts to Instagram using the Express content calendar. Coming soon: additional channels."
				statusLabel={
					isExpressInstagramExpiringSoon
						? `Reconnect needed — ${expressLabel} expires soon`
						: expressLabel
				}
				connected={hasValidInstagramConnection}
				onConnect={handleConnectExpress}
				onDisconnect={handleDisconnectExpress}
				connectDisabled={!expressApi}
				onReconnect={
					isExpressInstagramExpiringSoon ? handleReconnectExpress : undefined
				}
				statusVariant={isExpressInstagramExpiringSoon ? 'notice' : undefined}
			/>
			<IntegrationRow
				logo={<InstagramLogo className="h-6 w-6" />}
				name="Instagram"
				description="Pull in analytics, monitor comments, and send comments and DMs on your behalf."
				statusLabel={
					instagramNeedsReconnect ? instagramReconnectLabel : instagramLabel
				}
				connected={isInstagramConnected}
				onConnect={handleConnectInstagram}
				onDisconnect={handleDisconnectInstagram}
				onReconnect={
					instagramNeedsReconnect ? handleReconnectInstagram : undefined
				}
				statusVariant={instagramNeedsReconnect ? 'notice' : undefined}
			/>
			<IntegrationRow
				logo={<SquareLogo className="h-6 w-6" />}
				name="Square"
				description="Pull your customers into the People list to enable audience creation and marketing emails to those groups. Bridgeway automatically identifies lost, top, and new customers as audiences."
				statusLabel={squareLabel}
				detail={squareDetail}
				callout={squareCallout}
				connected={isSquareConnected}
				onConnect={handleConnectSquare}
				onDisconnect={handleDisconnectSquare}
				onReconnect={squareNeedsReconnect ? handleReconnectSquare : undefined}
				// Revocation ONLY, not the lapsed-token state beside it. A lapsed token is a
				// transient fault the merchant did not choose, and Reconnect really is the one
				// useful action there. A revocation is different: Square reports a merchant's
				// own disconnect exactly like any other, so we cannot tell the two apart, and
				// this row has to serve someone who meant to leave. Their notice and their
				// Settings mark both read from the stored connection, the client may not clear
				// `square_revoked_at`, and Disconnect is the only thing that clears either.
				allowDisconnectWhileReconnecting={squareIsRevoked}
				// Revocation is stronger than the amber "needs attention" the other rows use:
				// data has already stopped flowing, and only the merchant can restart it.
				statusVariant={
					squareIsRevoked
						? 'negative'
						: squareNeedsReconnect
							? 'notice'
							: undefined
				}
			/>
		</div>
	);
});
